import { Box, useToken } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { IoEyeOutline } from 'react-icons/io5';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import DataTable from '@/components/Organisms/DataTable';
import type {
	ColumnData,
	onFilterApplyProps,
} from '@/components/Organisms/DataTable/DataTable.types';
import { useGetFrontendErrorReports } from '@/hooks/service-hooks/appCrashReport';
import usePermission from '@/hooks/usePermission';
import type { AppCrashReport } from '@/services/appCrashReport';
import { getFrontendErrorReports } from '@/services/appCrashReport';
import type { FilterType } from '@/types/table';
import { formatDateTime } from '@/utils';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import { getFriendlyErrorMessage } from '@/utils/friendlyErrorMessages';
import AppCrashReportDetails from './AppCrashReportDetails';
import { appCrashReportColumns, ENV_MAP, moduleFilterOptions } from './reports';

const AppCrashReportTable = () => {
	const [brand600] = useToken('colors', ['brand.600']);
	const { hasPermission } = usePermission();
	const hasTechnicalAlertsPermission = hasPermission([
		'TechnicalAlerts:Visualize',
	]);

	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		search: '',
		enabled: true,
		status: '',
		globalFilters: '[]',
		sort: '',
		dateRange: {
			startDate: '',
			endDate: '',
		},
	});

	// Modal state for details view
	const [modalInformation, setModalInformation] = useState<{
		mode: 'details' | null;
		data: string | null;
	}>({
		mode: null,
		data: null,
	});

	const handleCloseModal = () => {
		setModalInformation({ mode: null, data: null });
	};

	// Fetch data from API
	const { data, isFetching } = useGetFrontendErrorReports({
		page: filters.page,
		limit: filters.limit,
		search: filters.search,
		dateRange: filters.dateRange,
		type: filters.status as string,
		enabled: filters.enabled,
	});

	// Transform API data to table format
	const transformedData = useMemo(() => {
		if (!data?.items) return [];
		return data.items.map((item) => ({
			...item,
			type: item.type || '-',
			environment: item.environment || '',
			moduleName: (
				<FaceliftTruncatedTextWithTooltip
					text={item.moduleName || '-'}
					maxLength={30}
				/>
			),
			location: (
				<FaceliftTruncatedTextWithTooltip
					text={item.location || '-'}
					maxLength={30}
				/>
			),
			errorMessage: (
				<FaceliftTruncatedTextWithTooltip
					text={
						item.errorMessage
							? hasTechnicalAlertsPermission
								? item.errorMessage
								: getFriendlyErrorMessage(item.errorMessage).title
							: '-'
					}
					maxLength={30}
				/>
			),
			method: (
				<FaceliftTruncatedTextWithTooltip
					text={item.method || '-'}
					maxLength={30}
				/>
			),
			userName: (
				<FaceliftTruncatedTextWithTooltip
					text={item.userName || '-'}
					maxLength={20}
				/>
			),
			userAgent: (
				<FaceliftTruncatedTextWithTooltip
					text={item.userAgent || '-'}
					maxLength={30}
				/>
			),
			serviceName: (
				<FaceliftTruncatedTextWithTooltip
					text={item.serviceName || '-'}
					maxLength={30}
				/>
			),
			createdAt: formatDateTime(item.createdAt),
		}));
	}, [data?.items, hasTechnicalAlertsPermission]);

	// Handle filter apply
	const onFilterApply = ({
		page,
		limit,
		status,
		search = '',
		sort = null,
		columnsFilters = [],
		globalFilters = [],
		dateRange = {
			startDate: '',
			endDate: '',
		},
	}: onFilterApplyProps) => {
		setFilters({
			page,
			limit,
			columnsFilters: JSON.stringify(columnsFilters),
			search,
			enabled: true,
			status: status?.value,
			globalFilters: '[]',
			sort: sort ? `${sort.key}|${sort.direction}` : '',
			dateRange,
		});
	};

	// Handle selection
	const onSelection = (_ids: string[]) => {
		// Handle selection logic here if needed
	};

	// Transform data for export (removes JSX components, keeps raw values)
	const transformedDataToExport = (data: AppCrashReport[]) => {
		if (!data) return [];
		return data.map((item) => ({
			id: item.id || '-',
			type: item.type || '-',
			moduleName: item.moduleName || '-',
			userName: item.userName || '-',
			createdAt: item.createdAt
				? formatDateTime(item.createdAt)
				: item.timestamp || '-',
			method: item.method || '-',
			serviceName: item.serviceName || '-',
			errorMessage: item.errorMessage || '-',
			userAgent: item.userAgent || '-',
			environment: ENV_MAP[item.environment || ''] || item.environment || '-',
			component: item.location || item.component || '-',
		}));
	};

	// Export functions
	const exportAllAsCSV = async () => {
		try {
			// Fetch all data with current filters
			const exportData = await getFrontendErrorReports(
				1,
				9999,
				filters.search || '',
				filters.dateRange,
				filters.status as string | undefined,
			);

			const transformedData = transformedDataToExport(exportData?.items || []);
			generateCSVFromTable(
				appCrashReportColumns,
				transformedData,
				'AppCrashReports-all',
				true,
			);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			toast.error('Failed to export CSV');
		}
	};

	const exportAllAsExcel = async () => {
		try {
			// Fetch all data with current filters
			const exportData = await getFrontendErrorReports(
				1,
				9999,
				filters.search || '',
				filters.dateRange,
				filters.status as string | undefined,
			);

			const transformedData = transformedDataToExport(exportData?.items || []);
			generateExcelFromTable(
				appCrashReportColumns,
				transformedData,
				'AppCrashReports-all',
				true,
			);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			toast.error('Failed to export Excel');
		}
	};

	return (
		<Box
			p={{ base: 4, md: 8 }}
			py={{ base: 4, md: 6 }}
			bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
		>
			<DataTable
				tableName="CrashReports"
				topLabel="App Crash Logs"
				loading={isFetching}
				onSelection={onSelection}
				items={transformedData}
				columns={appCrashReportColumns}
				totalCount={data?.meta?.totalRecords || 0}
				onFilterApply={onFilterApply}
				statusFilters={moduleFilterOptions}
				actions={[
					{
						label: 'Details',
						icon: <IoEyeOutline />,
						actionFn: (item: ColumnData) => {
							setModalInformation({
								mode: 'details',
								data: item._id as string,
							});
						},
					},
				]}
				globalDateFilters={[
					{
						label: 'Year to Date',
						value: 'year_to_date',
					},
					{
						label: 'This Week',
						value: 'this_week',
					},
					{
						label: 'Today',
						value: 'today',
					},
					{
						label: 'Last Month',
						value: 'last_month',
					},
					{
						label: 'Month to Date',
						value: 'month_to_date',
					},
					{
						label: 'Custom',
						value: 'custom',
					},
				]}
				hideAllStatusFilter
				hideAllColumnFilters
				bordered
				searchable
				hideShowColumn
				expandable
				paginated
				fullscreen
				exportable
				csv
				excel
				exportAllAsCSV={exportAllAsCSV}
				exportAllAsExcel={exportAllAsExcel}
			/>

			{/* Details Modal */}
			<FaceliftDialog
				showBackBtn={true}
				title="Crash Report Details"
				open={modalInformation.mode === 'details'}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				sx={{
					width: '70vw',
					maxWidth: '70vw',
					maxHeight: '90vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowY: 'auto',
				}}
			>
				<AppCrashReportDetails
					key={modalInformation.data}
					id={modalInformation.data}
				/>
			</FaceliftDialog>
		</Box>
	);
};

export default AppCrashReportTable;
